import { ChainId } from '@src/configs/chainInfo'
import { WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { LZ_CHAIN_IDS } from '@src/configs/chainInfo'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { ExchangeType, LiquiditySource } from '@src/enums'
import { IExchangeData } from '@src/types/exchange'
import { INetwork } from '@src/types/network'
import { IQuoteData } from '@src/types/order'
import { Address, IToken } from '@src/types/token'
import { getChecksumAddress, isTronAddress } from '@src/utils/address'
import { addressToBytes32 } from '@src/utils/addressBytes32'
import { Balance, computeUSDAmount } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'
import { exchangeLogger } from '@src/utils/logger'
import { solanaExchangeUtils } from '@src/utils/solana/exchangeUtils'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { TronConverter } from '@src/utils/tron/tronAddressConverter'

// Checks if there are any errors in the exchange
export const computeHasErrors = (
    isInsufficientBalance: boolean,
    isQuoteInvalid: boolean,
    isBuyOutputLoading: boolean,
    isQuoteExpired: boolean,
    isDestinationSolana: boolean,
    sellTokenSymbol?: string
) => {
    return (
        isInsufficientBalance ||
        isQuoteInvalid ||
        isBuyOutputLoading ||
        isQuoteExpired ||
        (isDestinationSolana && sellTokenSymbol !== 'USDC')
    )
}

// Computes the miles based on the sell balance, sell value, tokens, and referral code
export const computeMiles = (
    buyData: IExchangeData,
    sellData: IExchangeData,
    tokens: IToken[],
    boundReferralCode: string
): { miles: number; multiplier: number } => {
    // Return 0 miles for wrap/unwrap operations
    if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
        return { miles: 0, multiplier: 1 }
    }

    // Fallback implementation using local calculations
    // This is kept as a fallback if the API call fails
    const fallbackCalculation = () => {
        // Get USD price of the associated token from allUSDPrices
        const token = tokens.find(o => o.address === sellData.token.address && o.chainId === sellData.token.chainId)

        if (token && token.price_current && token.price_current > 0) {
            const miles = computeUSDAmount(token, sellData.value) ?? 0
            // if source/dest chain is Solana/Tron/Unichain, apply 2x multiplier regardless of referral code
            const isDoubledMilesChainsInvolved = [ChainId.Solana, ChainId.Tron, ChainId.Unichain].some(chain =>
                [sellData.chainId, buyData.chainId].includes(chain)
            )

            const baseMultiplier = boundReferralCode === 'MACH' ? 1.05 : boundReferralCode ? 1.15 : 1
            const multiplier = isDoubledMilesChainsInvolved ? baseMultiplier * 2 : baseMultiplier

            return { miles: miles * multiplier, multiplier }
        }

        return { miles: 0, multiplier: 1 }
    }

    // Return a promise-compatible result that mimics the shape of the async function result
    // but synchronously returns the fallback calculation for backward compatibility
    return fallbackCalculation()
}

// Checks if the sell and buy tokens are potentially CCTP compatible
export const isPotentiallyCCTP = (
    sellToken: string | undefined,
    buyToken: string | undefined,
    sellNetworkId: number | string,
    buyNetworkId: number | string,
    liquiditySource?: LiquiditySource
): boolean => {
    // A real CCTP transfer requires:
    // 1. Both tokens are USDC
    // 2. They're on different chains
    // 3. The liquiditySource is specifically cctpDirect

    const tokensMatch = sellToken === 'USDC' && buyToken === 'USDC'
    const differentChains = sellNetworkId !== buyNetworkId
    const isCctpSource = liquiditySource === LiquiditySource.cctpDirect

    // If liquiditySource is provided, use it to determine if this is definitely CCTP
    if (liquiditySource !== undefined) {
        return tokensMatch && differentChains && isCctpSource
    }

    // If no liquiditySource is provided, we can only make a guess based on tokens and chains
    // (this is the legacy behavior, better to provide liquiditySource when possible)
    return tokensMatch && differentChains
}

// Formats the exchange amount with commas
export const formatExchangeAmountWithCommas = (
    data: IExchangeData,
    maxDecimals: number = MAX_TOKEN_DISPLAY_DECIMALS
): string => {
    return new Balance(data.token).setValue(data.value).formatWithCommas(maxDecimals)
}

/**
 * Returns the appropriate swap function parameters based on exchange type and other data.
 * @param exchangeType
 * @param walletAddress
 * @param sellData
 * @param buyData
 * @param quote
 * @param gasEstimate
 * @param availableNetworks
 * @returns
 */
export const getSwapFunctionParameters = async (
    exchangeType: string,
    walletAddress: string | undefined,
    sellData: IExchangeData,
    buyData: IExchangeData,
    quote: IQuoteData,
    gasEstimate: boolean = false,
    availableNetworks: Record<string, INetwork>,
    flattened: boolean = false
) => {
    exchangeLogger.debug('[getTransactionFunctionArgs] [setMax]: received params:', {
        exchangeType: exchangeType,
        walletAddress: walletAddress,
        sellData: sellData,
        buyData: buyData,
        quote: quote,
    })

    let swapFunctionArgs

    if (exchangeType === ExchangeType.order_book) {
        swapFunctionArgs = encodePlaceOrderParameters(sellData, buyData, quote, gasEstimate, flattened)
    } else if (exchangeType === ExchangeType.cctp) {
        if (buyData.chainId === ChainId.Solana) {
            swapFunctionArgs = solanaExchangeUtils.getSwapFunctionParameters!(
                walletAddress,
                sellData,
                buyData,
                availableNetworks,
                flattened
            )
        } else {
            const amount = sellData.value || BigInt(0)
            const destinationAddressInBytes32 = addressToBytes32(walletAddress || '0x', buyData.chainId)

            if (flattened) {
                // Flattened arguments
                swapFunctionArgs = [
                    { type: 'uint256', value: amount.toString() },
                    { type: 'uint32', value: availableNetworks[buyData.chainId].cctp_id.toString() },
                    { type: 'bytes32', value: destinationAddressInBytes32 },
                    { type: 'address', value: mapTokenAddress(sellData.token.address, false, sellData.chainId) },
                ]
            } else {
                swapFunctionArgs = [
                    amount,
                    availableNetworks[buyData.chainId].cctp_id,
                    destinationAddressInBytes32,
                    mapTokenAddress(sellData.token.address, false, sellData.chainId),
                ]
            }
        }
    }

    exchangeLogger.debug('[getSwapFunctionParameters]: swapFunctionArgs=', {
        swapFunctionArgs,
    })
    return swapFunctionArgs
}

/**
 * Encodes contract call parameters for `placeOrder((address,address,uint32),(uint96,uint96,uint16,address,uint96),(uint32,uint16,uint16),address,address)`
 * @param sellData
 * @param buyData
 * @param quoteData
 * @param gasEstimate
 * @returns
 */
export const encodePlaceOrderParameters = (
    sellData: IExchangeData,
    buyData: IExchangeData,
    quote: IQuoteData,
    gasEstimate: boolean = false,
    flattened: boolean = false
): any[] => {
    exchangeLogger.debug('[DEBUG] encodePlaceOrderParameters INPUTS:', {
        sellData: JSON.stringify(sellData),
        buyData: JSON.stringify(buyData),
        quote: JSON.stringify(quote),
        gasEstimate,
        flattened,
    })

    let returnValue: any[] = []

    // Skip validation if doing gas estimation
    if (!gasEstimate) {
        if (quote.reject_order) {
            throw new Error('Swap amount is too low to cover transaction fees.')
        }

        if (
            getChecksumAddress(mapTokenAddress(sellData.token.address, true, sellData.chainId)) !==
                getChecksumAddress(mapTokenAddress(quote.src_asset_address, true, sellData.chainId)) ||
            getChecksumAddress(mapTokenAddress(buyData.token.address, true, buyData.chainId)) !==
                getChecksumAddress(mapTokenAddress(quote.dst_asset_address, true, buyData.chainId))
        ) {
            throw new Error('Invalid quote: token address mismatch')
        }

        if (LZ_CHAIN_IDS[buyData.token.chainId] < 0) {
            throw new Error(`No LayerZero chain id found for token: ${JSON.stringify(buyData.token)}`)
        }
    }

    // Access the nested properties from quote.order_data to fix TypeScript errors
    // In V2 API, some properties have different names (src_filler_address instead of filler_address)
    const { order_funding, order_direction, order_expiration } = quote.order_data || {}
    // Get filler_address from the appropriate property based on API version
    const filler_address = (quote.order_data?.src_filler_address || quote.order_data?.filler_address) as
        | string
        | undefined
    const _srcAssetAddress = sellData.token.address || ''
    const _dstAssetAddress = buyData.token.address || ''
    // Accessing target_address from the correct location in the data structure
    // Use type assertion to avoid TypeScript errors
    const target_address = ((quote as any).order_data?.target_address || (quote as any).target_address || '') as string

    // Output debug info directly to console to make sure it appears
    console.log('==================== V2 MIGRATION DEBUG ====================')
    console.log('SOURCE ADDRESS VALUES:', {
        srcAssetAddress: _srcAssetAddress ? _srcAssetAddress : '[UNDEFINED]',
        dstAssetAddress: _dstAssetAddress ? _dstAssetAddress : '[UNDEFINED]',
        targetAddress: target_address ? target_address : '[UNDEFINED]',
        featureEnabled: isFeatureEnabled('V2_MIGRATION'),
        buyChainId: buyData.chainId,
        sellChainId: sellData.chainId,
        orderData: quote.order_data ? 'present' : '[UNDEFINED]',
        orderDirection: order_direction ? JSON.stringify(order_direction) : '[UNDEFINED]',
        quoteData: JSON.stringify(quote),
    })
    console.log('===========================================================')

    exchangeLogger.debug('[encodePlaceOrderParameters] order_funding:', order_funding)
    exchangeLogger.debug('[encodePlaceOrderParameters] order_expiration:', order_expiration)
    exchangeLogger.debug('[encodePlaceOrderParameters] filler_address:', filler_address)
    exchangeLogger.debug('[encodePlaceOrderParameters] target_address:', target_address)

    // Use the canonical src_token_address from order_direction (matches original logic)
    const srcAssetAddress = order_direction.src_token_address || _srcAssetAddress
    console.log('[DEBUG] srcAssetAddress values:', {
        fromOrderDirection: order_direction.src_token_address,
        fromSellData: _srcAssetAddress,
        srcAssetAddress,
        isUndefinedOrEmpty: !srcAssetAddress,
        order_direction,
    })

    // Use the already-parsed and normalized dst_asset_address from quote
    let dstAssetAddress = quote.dst_asset_address

    // Special handling for Tron: convert to EVM checksum format if needed
    // Tron addresses are not EVM-compatible by default, so we convert if the destination is Tron
    if (buyData.token.chainId === ChainId.Tron) {
        if (buyData.token && isTronAddress(dstAssetAddress)) {
            dstAssetAddress = TronConverter.toChecksumEVM(dstAssetAddress) as Address
        } else {
            exchangeLogger.warn(
                'Tron address expected here, because buyData.token.chainId=ChainId.Tron. But received:',
                {
                    address: dstAssetAddress,
                }
            )
        }
    }

    exchangeLogger.debug('[encodePlaceOrderParameters] buyData.chainId:', buyData.chainId)

    // For all chains, use dstAssetAddress after Tron logic
    exchangeLogger.debug('[encodePlaceOrderParameters] dstAssetAddress:', dstAssetAddress)

    let dstAsset: `0x${string}` | string
    if (
        isFeatureEnabled('V2_MIGRATION') &&
        typeof dstAssetAddress === 'string' &&
        dstAssetAddress.startsWith('0x') &&
        dstAssetAddress.length === 66
    ) {
        // When using V2 API, addresses may already be in bytes32 format
        exchangeLogger.debug('[encodePlaceOrderParameters] Using pre-formatted bytes32 address from V2 API')
        dstAsset = dstAssetAddress as `0x${string}`
    } else {
        // Convert to bytes32 format (for V1 API or non-bytes32 addresses)
        exchangeLogger.debug('[DEBUG] Converting dstAssetAddress to bytes32:', {
            dstAssetAddress: dstAssetAddress,
            chainId: buyData.chainId,
        })
        exchangeLogger.debug('[FOCUSED DEBUG] addressToBytes32 input:', {
            address: mapTokenAddress(dstAssetAddress, true, buyData.chainId),
            chainId: buyData.chainId,
        })
        dstAsset = addressToBytes32(mapTokenAddress(dstAssetAddress, true, buyData.chainId), buyData.chainId)
        exchangeLogger.debug('[FOCUSED DEBUG] addressToBytes32 output:', {
            result: dstAsset,
        })
    }

    exchangeLogger.debug('[encodePlaceOrderParameters] dstAsset:', dstAsset)

    let targetAddressValue: `0x${string}` | string

    exchangeLogger.debug('[V2_DEBUG] Target address check:', {
        targetAddress: target_address ? target_address : '[UNDEFINED]',
        isV2Enabled: isFeatureEnabled('V2_MIGRATION'),
        hasTargetAddress: !!target_address,
        startsWithHex: target_address ? target_address.startsWith('0x') : false,
        length: target_address ? target_address.length : 0,
        isBytes32Format:
            isFeatureEnabled('V2_MIGRATION') &&
            target_address &&
            target_address.startsWith('0x') &&
            target_address.length === 66,
    })

    if (
        isFeatureEnabled('V2_MIGRATION') &&
        target_address &&
        target_address.startsWith('0x') &&
        target_address.length === 66
    ) {
        // Already in bytes32 format, use as is
        exchangeLogger.debug('[encodePlaceOrderParameters] Using pre-formatted bytes32 target address from V2 API')
        targetAddressValue = target_address as `0x${string}`
    } else {
        // Convert to bytes32 format (for V1 API or non-bytes32 addresses)
        // If target_address is empty, use a properly formatted zero bytes32 value
        if (!target_address) {
            exchangeLogger.debug('[V2_DEBUG] Empty target_address, using zero bytes32')
            // Proper zero bytes32 value
            targetAddressValue = '0x0000000000000000000000000000000000000000000000000000000000000000'
        } else {
            exchangeLogger.debug('[V2_DEBUG] Converting target_address to bytes32:', {
                targetAddress: target_address,
                chainId: buyData.chainId,
            })
            try {
                targetAddressValue = addressToBytes32(target_address, buyData.chainId)
                exchangeLogger.debug('[V2_DEBUG] Successfully converted target_address:', targetAddressValue)
            } catch (error) {
                exchangeLogger.error('[V2_DEBUG] Failed to convert target_address:', error)
                // Fallback to zero bytes32 value on conversion error
                targetAddressValue = '0x0000000000000000000000000000000000000000000000000000000000000000'
            }
        }
    }

    exchangeLogger.debug('[encodePlaceOrderParameters] targetAddressValue:', targetAddressValue)

    // Direct console logging of final values before they're used
    console.log('==================== FINAL ADDRESS VALUES ====================')
    console.log('FINAL ADDRESS VALUES:', {
        srcAsset: mapTokenAddress(srcAssetAddress, true, sellData.chainId),
        dstAsset,
        targetAddressValue,
        fillerAddress: filler_address,
        orderFunding: order_funding ? JSON.stringify(order_funding) : '[UNDEFINED]',
        orderDirection: order_direction ? JSON.stringify(order_direction) : '[UNDEFINED]',
    })
    console.log('=============================================================')

    // Safeguard against undefined addresses
    // Create safe local variables for all critical addresses
    const safeFillerAddress = filler_address || '0x0000000000000000000000000000000000000000'
    if (!filler_address) {
        console.log('WARNING: filler_address is undefined, using zero address fallback')
    }

    if (flattened) {
        returnValue = [
            {
                type: 'address',
                value: mapTokenAddress(srcAssetAddress, true, sellData.chainId),
            },
            {
                type: 'bytes32',
                value: dstAsset,
            },
            { type: 'uint32', value: order_direction.dst_lzc.toString() },
            { type: 'uint96', value: BigInt(order_funding.src_amount_in).toString() },
            {
                type: 'uint96',
                value: BigInt(order_funding.dst_amount_out).toString(),
            },
            { type: 'uint16', value: order_funding.bond_fee.toString() },
            { type: 'address', value: order_funding.bond_token_address },
            { type: 'uint96', value: BigInt(order_funding.bond_amount).toString() },
            { type: 'uint32', value: order_expiration.timestamp.toString() },
            { type: 'uint16', value: order_expiration.challengeOffset.toString() },
            { type: 'uint16', value: order_expiration.challengeWindow.toString() },
            {
                type: 'bytes32',
                value: targetAddressValue,
            },
            { type: 'address', value: safeFillerAddress },
        ]
    } else {
        returnValue = [
            [mapTokenAddress(_srcAssetAddress, true, sellData.chainId), dstAsset, order_direction.dst_lzc],
            [
                // Add safety checks to avoid BigInt conversion errors
                order_funding.src_amount_in ? BigInt(order_funding.src_amount_in) : BigInt(0),
                order_funding.dst_amount_out ? BigInt(order_funding.dst_amount_out) : BigInt(0),
                order_funding.bond_fee ?? 0,
                order_funding.bond_token_address ?? '0x0000000000000000000000000000000000000000',
                order_funding.bond_amount ? BigInt(order_funding.bond_amount) : BigInt(0),
            ],
            [order_expiration.timestamp, order_expiration.challengeOffset, order_expiration.challengeWindow],
            targetAddressValue,
            safeFillerAddress,
        ]
    }

    exchangeLogger.debug('[encodePlaceOrderParameters]: returnValue=', {
        returnValue,
    })
    return returnValue
}

// Checks if the sell and buy tokens are wrapped only
export const isWrapOnly = (sellData: IExchangeData, buyData: IExchangeData) => {
    return (
        sellData.chainId === buyData.chainId &&
        buyData.token.address.toLowerCase() === WRAPPED_GAS_TOKENS[buyData.chainId].address.toLowerCase() &&
        sellData.token.native === true
    )
}

// Checks if the sell and buy tokens are unwrapped only
export const isUnWrapOnly = (sellData: IExchangeData, buyData: IExchangeData) => {
    return (
        sellData.chainId === buyData.chainId &&
        sellData.token.address.toLowerCase() === WRAPPED_GAS_TOKENS[sellData.chainId].address.toLowerCase() &&
        buyData.token.native === true
    )
}
